/**
 * planLabel — Feb 2027 · Returns the display label for the current
 * business's plan. When a business has graduated into Operating Mode
 * (`business.mode === "operating"`), the label becomes "Strategic Plan"
 * to signal the shift in intent (from launch prep to run-the-business
 * strategy). Launching-mode plans keep the classic "Business Plan"
 * label.
 *
 * Every UI surface that names the plan in-context (page masthead,
 * hub tile, back-links, breadcrumbs, command palette) should pull
 * its label from here so the terminology stays consistent across
 * the app.
 *
 * Marketing / public / admin pages that talk about business plans
 * in general (LandingPage, PublicExamplesPage, admin lists) should
 * NOT use this helper — they refer to the product category, not the
 * founder's specific plan.
 */
export function planLabel(business) {
  return (business?.mode || "launching") === "operating"
    ? "Strategic Plan"
    : "Business Plan";
}

export function planLabelLower(business) {
  return (business?.mode || "launching") === "operating"
    ? "strategic plan"
    : "business plan";
}

/**
 * roadmapLabel — Feb 2027 · Companion to planLabel for the Roadmap
 * document surfaces. Launching-mode plans read "Execution Roadmap"
 * (the classic label); operating-mode businesses see the roadmap
 * renamed to "Operating Roadmap" to signal the shift in intent
 * (from launch prep to run-the-business execution) — matching the
 * "Strategic Plan" rename applied to the plan itself.
 *
 * Note: the Hub tile in operating mode already uses "Operations"
 * (a broader category label covering roadmap + activity + reports).
 * This helper is for the Roadmap DOCUMENT surfaces specifically —
 * page eyebrow, back-links, breadcrumbs, command palette entry.
 */
export function roadmapLabel(business) {
  return (business?.mode || "launching") === "operating"
    ? "Operating Roadmap"
    : "Execution Roadmap";
}

/**
 * goalsLabel — Feb 2027 · In Operating Mode, "Goals" become
 * "Strategic Goals" — a classification-driven concept sitting
 * beneath the Strategic Plan. Launching-mode businesses keep the
 * classic "Goals" label. Used on the GoalsSection header, sidebar,
 * hub tile, breadcrumb, command palette.
 */
export function goalsLabel(business) {
  return (business?.mode || "launching") === "operating"
    ? "Strategic Goals"
    : "Goals";
}

/**
 * projectsLabel — Feb 2027 (v2) · Now always returns "Projects" per
 * founder feedback: the "Experiments" label was product jargon and
 * confused new founders. Kept as a helper (rather than inlining
 * "Projects" everywhere) so callers still have a single seam for
 * future re-branding without touching UI code.
 */
export function projectsLabel(_business) {
  return "Projects";
}

/**
 * projectLabelSingular — Feb 2027 (v2) · Singular companion — see
 * `projectsLabel`. Always returns "Project".
 */
export function projectLabelSingular(_business) {
  return "Project";
}

/**
 * planLink — Feb 2027 · The primary "Strategic Plan" / "Business
 * Plan" link target. In Operating Mode, the destination honors the
 * founder's Settings pref (`hub_prefs.strategic_plan_view`):
 *   - "hierarchy" (default) → `/business/:id/strategic-plan/hierarchy`
 *   - "sections" → `/business/:id/plan` (classic sectioned view)
 *
 * In Launching Mode, `hub_prefs.business_plan_view` picks between:
 *   - "sections" (default) → `/business/:id/plan` (classic sectioned view)
 *   - "hierarchy" → `/business/:id/plan/hierarchy` (Pre-Launch Hierarchy)
 *
 * `hubPrefs` is optional — when not supplied (e.g. still loading),
 * the function returns the classic sectioned view for launching mode
 * and the base `/strategic-plan` route for operating mode (which then
 * redirects based on the founder's saved pref, defense-in-depth).
 * Callers who have prefs in hand should pass them so the founder
 * skips the redirect flash and lands on the correct surface first.
 */
export function planLink(business, id, hubPrefs) {
  const operating = (business?.mode || "launching") === "operating";
  if (!operating) {
    // Jun 2026 · The Overview (Pre-Launch Hierarchy) is now the default
    // Business Plan view. Only an explicit "sections" pref lands on the
    // classic sectioned page.
    if (hubPrefs && hubPrefs.business_plan_view === "sections") {
      return `/business/${id}/plan`;
    }
    return `/business/${id}/plan/hierarchy`;
  }
  if (hubPrefs && hubPrefs.strategic_plan_view === "sections") {
    return `/business/${id}/plan`;
  }
  if (hubPrefs && hubPrefs.strategic_plan_view === "hierarchy") {
    return `/business/${id}/strategic-plan/hierarchy`;
  }
  return `/business/${id}/strategic-plan`;
}
